import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const cart = useSelector((state) => state.cart.cart);
  const uniqueProducts = Array.from(new Set(cart.map((item) => item.ID))).length;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const togglePolicies = () => {
    setPoliciesOpen((prev) => !prev);
  };

  const navigateTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white shadow-lg py-3"
          : "bg-gradient-to-r from-green-50 to-emerald-50 py-4"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Menu Button and Brand Name */}
          <div className="flex items-center space-x-4">
            <button
              ref={menuButtonRef}
              className="p-2 rounded-lg hover:bg-green-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6 text-green-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <h1
  onClick={() => navigateTo("/")}
  className="text-xl font-bold text-green-800 cursor-pointer hover:text-green-600 transition-colors duration-200"
>
  Lihen Pharmacy
</h1>

          </div>

          {/* Cart Icon - Always visible */}
          <div
            className="relative p-2 cursor-pointer group"
            onClick={() => navigate("/cart")}
          >
            <div className="relative">
              <svg
                className="w-7 h-7 text-green-700 group-hover:text-green-600 transition-colors duration-200"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>

              {uniqueProducts > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-200">
                  {uniqueProducts}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Contains all navigation items */}
      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 right-0 bg-white shadow-xl border-t border-green-100 animate-slideDown"
        >
          <div className="container mx-auto px-4 py-3">
            <ul className="space-y-1">
              <li>
                <button
                  onClick={() => navigateTo("/")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo("/drugs")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                    />
                  </svg>
                  Products
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo("/drugs")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                  Buy Drugs Online
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo("/Aboutus")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo("/Contact")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Contact Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo("/Chatwithapharm")}
                  className="w-full text-left px-4 py-3 text-green-800 hover:bg-green-50 rounded-lg transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                  Chat with a Pharmacist
                </button>
              </li>

              {/* Policies Section */}
              <li>
                <div className="px-4 py-3">
                  <button
                    onClick={togglePolicies}
                    className="w-full text-left text-green-800 flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-5 h-5 mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2v-5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Policies
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${
                        policiesOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {policiesOpen && (
                    <div className="pl-8 mt-2 space-y-2">
                      <button
                        onClick={() => navigateTo("/Termsandconditions")}
                        className="block w-full text-left py-2 text-green-700 hover:text-green-600 transition-colors duration-200"
                      >
                        Terms and Conditions
                      </button>
                      <button
                        onClick={() => navigateTo("/Privacypolicy")}
                        className="block w-full text-left py-2 text-green-700 hover:text-green-600 transition-colors duration-200"
                      >
                        Privacy Policy
                      </button>
                      <button
                        onClick={() => navigateTo("/Refundpolicy")}
                        className="block w-full text-left py-2 text-green-700 hover:text-green-600 transition-colors duration-200"
                      >
                        Refund Policy
                      </button>
                    </div>
                  )}
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
      `}</style>
    </header>
  );
};

export default Header;
